import React, { useState, useEffect } from 'react';
import { 
  Newspaper, Plus, Pencil, Trash2, Eye, EyeOff, Star,
  Calendar, Clock, Loader2, Search, Filter, Video, Image as ImageIcon
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ImageUploader from '../components/ImageUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Tecnología', 'Proyectos', 'Empresa', 'Industria', 'Eventos', 'Tips'];

const NewsPage = () => {
  const { token } = useAuth();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'Empresa',
    image_url: '',
    video_url: '',
    author: 'Zenex',
    is_published: false,
    is_featured: false,
    publish_date: '',
    tags: ''
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/news`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNews(response.data);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: editingNews ? formData.slug : generateSlug(title)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSend = {
        ...formData,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [],
        publish_date: formData.publish_date || null
      };
      
      if (editingNews) {
        await axios.put(`${API_URL}/api/news/${editingNews.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/news`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowModal(false);
      setEditingNews(null);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error('Error saving news:', error);
      alert(error.response?.data?.detail || 'Error al guardar la noticia');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta noticia?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/news/${newsId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNews();
    } catch (error) {
      console.error('Error deleting news:', error);
    }
  };

  const togglePublish = async (newsItem) => {
    try {
      await axios.post(`${API_URL}/api/news/${newsItem.id}/toggle-publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNews();
    } catch (error) {
      console.error('Error toggling publish:', error);
    }
  };

  const toggleFeatured = async (newsItem) => {
    try {
      await axios.put(`${API_URL}/api/news/${newsItem.id}`, 
        { is_featured: !newsItem.is_featured },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNews();
    } catch (error) {
      console.error('Error toggling featured:', error);
    }
  };

  const openEditModal = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      slug: newsItem.slug,
      excerpt: newsItem.excerpt,
      content: newsItem.content,
      category: newsItem.category,
      image_url: newsItem.image_url || '',
      video_url: newsItem.video_url || '',
      author: newsItem.author,
      is_published: newsItem.is_published,
      is_featured: newsItem.is_featured,
      publish_date: newsItem.publish_date ? newsItem.publish_date.split('T')[0] : '',
      tags: newsItem.tags ? newsItem.tags.join(', ') : ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'Empresa',
      image_url: '',
      video_url: '',
      author: 'Zenex',
      is_published: false,
      is_featured: false,
      publish_date: '',
      tags: ''
    });
  };

  const seedNews = async () => {
    try {
      await axios.post(`${API_URL}/api/news/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchNews();
    } catch (error) {
      console.error('Error seeding news:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString('es-PY', {
        day: 'numeric', month: 'short', year: 'numeric'
      });
    } catch {
      return null;
    }
  };

  const isScheduled = (newsItem) => {
    if (!newsItem.publish_date) return false;
    return new Date(newsItem.publish_date) > new Date();
  };

  // Filter news
  const filteredNews = news.filter(n => {
    const matchesSearch = n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         n.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !filterCategory || n.category === filterCategory;
    const matchesStatus = !filterStatus || 
      (filterStatus === 'published' && n.is_published) ||
      (filterStatus === 'draft' && !n.is_published) ||
      (filterStatus === 'scheduled' && isScheduled(n));
    return matchesSearch && matchesCategory && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="news-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Noticias</h1>
          <p className="text-zinc-500">Gestiona las noticias que aparecen en el sitio web</p>
        </div>
        <div className="flex gap-3">
          {news.length === 0 && (
            <button
              onClick={seedNews}
              className="px-4 py-2 bg-zinc-100 text-zinc-700 rounded-lg hover:bg-zinc-200 transition-colors"
            >
              Cargar noticias de ejemplo
            </button>
          )}
          <button
            onClick={() => {
              resetForm();
              setEditingNews(null);
              setShowModal(true);
            }}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nueva Noticia
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-xl shadow-sm border border-zinc-200">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Buscar noticias..."
            className="w-full pl-10 pr-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">Todas las categorías</option>
          {CATEGORIES.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
        >
          <option value="">Todos los estados</option>
          <option value="published">Publicadas</option>
          <option value="draft">Borradores</option>
          <option value="scheduled">Programadas</option>
        </select>
      </div>

      {/* News List */}
      {filteredNews.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-zinc-200 p-12 text-center">
          <Newspaper className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-zinc-900 mb-2">No hay noticias</h3>
          <p className="text-zinc-500">Crea tu primera noticia o carga las de ejemplo</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredNews.map((newsItem) => (
            <div 
              key={newsItem.id}
              className={`bg-white rounded-xl shadow-sm border p-4 ${
                newsItem.is_published ? 'border-zinc-200' : 'border-zinc-100 opacity-70'
              }`}
            >
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                <div className="w-24 h-24 bg-zinc-100 rounded-lg flex-shrink-0 overflow-hidden">
                  {newsItem.image_url ? (
                    <img 
                      src={newsItem.image_url} 
                      alt={newsItem.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Newspaper className="w-8 h-8 text-zinc-300" />
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-zinc-900 truncate">{newsItem.title}</h3>
                    {newsItem.is_featured && (
                      <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Star className="w-3 h-3" /> Destacada
                      </span>
                    )}
                    {newsItem.video_url && (
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full flex items-center gap-1">
                        <Video className="w-3 h-3" /> Video
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-500 line-clamp-2 mb-2">{newsItem.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-zinc-400">
                    <span className="px-2 py-0.5 bg-zinc-100 text-zinc-600 rounded">{newsItem.category}</span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(newsItem.created_at)}
                    </span>
                    {newsItem.publish_date && (
                      <span className={`flex items-center gap-1 ${isScheduled(newsItem) ? 'text-blue-500' : ''}`}>
                        <Clock className="w-3 h-3" />
                        {isScheduled(newsItem) ? 'Programada: ' : 'Publicada: '}
                        {formatDate(newsItem.publish_date)}
                      </span>
                    )}
                    <span>{newsItem.views || 0} vistas</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleFeatured(newsItem)}
                    className={`p-2 rounded-lg transition-colors ${
                      newsItem.is_featured 
                        ? 'bg-yellow-100 text-yellow-600' 
                        : 'bg-zinc-100 text-zinc-400 hover:text-yellow-600'
                    }`}
                    title={newsItem.is_featured ? 'Quitar destacado' : 'Destacar'}
                  >
                    <Star className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => togglePublish(newsItem)}
                    className={`p-2 rounded-lg transition-colors ${
                      newsItem.is_published 
                        ? 'bg-emerald-100 text-emerald-600' 
                        : 'bg-zinc-100 text-zinc-400'
                    }`}
                    title={newsItem.is_published ? 'Ocultar' : 'Publicar'}
                  >
                    {newsItem.is_published ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                  </button>
                  <button
                    onClick={() => openEditModal(newsItem)}
                    className="p-2 bg-zinc-100 text-zinc-600 rounded-lg hover:bg-zinc-200 transition-colors"
                  >
                    <Pencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(newsItem.id)}
                    className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-zinc-200">
              <h2 className="text-xl font-bold text-zinc-900">
                {editingNews ? 'Editar Noticia' : 'Nueva Noticia'}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Título *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={handleTitleChange}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Slug (URL)
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Categoría
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Extracto / Resumen *
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Contenido Completo * (soporta HTML)
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  rows={8}
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-mono text-sm"
                  required
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Imagen de Portada
                  </label>
                  <ImageUploader
                    currentImage={formData.image_url}
                    onImageUploaded={(url) => setFormData({ ...formData, image_url: url })}
                    folder="news"
                  />
                  <input
                    type="url"
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    placeholder="O pega una URL de imagen..."
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 mt-2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Video (URL de YouTube o similar)
                  </label>
                  <input
                    type="url"
                    value={formData.video_url}
                    onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                    placeholder="https://youtube.com/..."
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-zinc-400 mt-1">El video se mostrará en la página de la noticia</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Autor
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-700 mb-1">
                    Fecha de Publicación
                  </label>
                  <input
                    type="date"
                    value={formData.publish_date}
                    onChange={(e) => setFormData({ ...formData, publish_date: e.target.value })}
                    className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                  <p className="text-xs text-zinc-400 mt-1">Dejar vacío para publicar inmediatamente. Fecha futura = programada.</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Tags (separados por coma)
                </label>
                <input
                  type="text"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="paneles, tecnología, empresa"
                  className="w-full px-4 py-2 border border-zinc-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              <div className="flex flex-col gap-3 p-4 bg-zinc-50 rounded-lg">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Publicada (visible en el sitio)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-500"
                  />
                  <span className="text-sm text-zinc-700">Destacada (aparecerá primero)</span>
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-zinc-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingNews(null);
                  }}
                  className="px-4 py-2 text-zinc-700 hover:bg-zinc-100 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {editingNews ? 'Guardar Cambios' : 'Crear Noticia'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsPage;
